import { Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { RoleGuard } from './guards/role.guard';
import { SuperAdminGuard } from './guards/super-admin.guard';
import { TunnelGuard } from './guards/tunnel.guard';

import { AppShellComponent } from './layout/app-shell/app-shell.component';
import { SuperAdminShellComponent } from './layout/super-admin-shell/super-admin-shell.component';

import { LoginComponent } from './auth/login/login.component';
import { ForgotPasswordComponent } from './auth/forgot-password/forgot-password.component';
import { ResendEmailComponent } from './auth/resend-email/resend-email.component';
import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { AuthBootstrapComponent } from './auth/auth-bootstrap/auth-bootstrap.component';
import { initialRedirectGuard } from './guards/initial-redirect.guard';

import { DashboardComponent } from './dashboard/dashboard.component';
import { ProfileComponent } from './profile/profile.component';
import { CustomerComponent } from './customer/customer.component';
import { PackageComponent } from './package/package.component';
import { DiscountComponent } from './discount/discount.component';
import { OrdersWrapperComponent } from './orders/orders-wrapper.component';
import { TunnelOrdersComponent } from './orders/tunnel-orders.component';
import { EmployeeComponent } from './employee/employee.component';
import { FleetComponent } from './fleet/fleet.component';
import { AttendanceComponent } from './attendance/attendance.component';
import { CameraComponent } from './camera/camera.component';
import { AppointmentsWrapperComponent } from './appointments/appointments-wrapper.component';
import { RewardsComponent } from './rewards/rewards.component';
import { SalesComponent } from './sales/sales.component';
import { StaffCashReconciliationComponent } from './components/staff/cash-reconciliation/cash-reconciliation.component';
import { CampaignComponent } from './campaign/campaign.component';
import { NotificationsListComponent } from './notifications/notifications-list.component';
import { AnalyticsComponent } from './analytics/analytics.component';
import { ReportsComponent } from './reports/reports.component';
import { CampaignEditorComponent } from './campaign/campaign-editor/campaign-editor.component';
import { CarWashWebsiteComponent } from './car-wash-website/car-wash-website.component';
import { WebsiteBuilderComponent } from './website-builder/website-builder.component';
import { WebsitePreviewPageComponent } from './car-wash-website/components/website-preview-page/website-preview-page.component';
import { EmailPreviewPageComponent } from './campaign/email-preview-page/email-preview-page.component';
import { SmsCampaignLandingPageComponent } from './campaign/sms-campaign-landing-page/sms-campaign-landing-page.component';
import { SmsCampaignSuccessPageComponent } from './campaign/sms-campaign-success-page/sms-campaign-success-page.component';
import { FeedbackFormPageComponent } from './feedback/feedback-form-page.component';
import { ReceiptPageComponent } from './receipt/receipt-page.component';
import { PrepaidPassPageComponent } from './prepaid-pass/prepaid-pass-page.component';
import { DocsComponent } from './docs/docs.component';
import { PublicStreamComponent } from './camera/public-stream/public-stream.component';
import { PaymentsComponent } from './payments/payments.component';
import { PaymentDashboardComponent } from './payments/payment-dashboard/payment-dashboard.component';
// The old Payouts page is retired: the Payout Tracker lists those payouts.
// import { PayoutsComponent } from './payments/payouts/payouts.component';
import { PayoutTrackerComponent } from './payments/payout-tracker/payout-tracker.component';

import { AdminLoginComponent } from './admin/login/admin-login.component';
import { TunnelLoginComponent } from './tunnel/login/tunnel-login.component';
import { AdminDashboardComponent } from './admin/dashboard/admin-dashboard.component';
import { AdminCustomersComponent } from './admin/customers/admin-customers.component';
import { AdminCampaignsComponent } from './admin/campaigns/admin-campaigns.component';
import { CompanyLocationComponent } from './admin/companies/company-detail/location/company-location.component';
import { legacyLocationSettingsRedirect } from './admin/companies/company-detail/location/legacy-settings.redirect';
import { AdminStatusComponent } from './admin/status/admin-status.component';
import { AdminCompaniesComponent } from './admin/companies/admin-companies.component';
import { AdminCompanyOnboardComponent } from './admin/companies/admin-company-onboard.component';
import { CompanyDetailComponent } from './admin/companies/company-detail/company-detail.component';
import { CompanyOverviewComponent } from './admin/companies/company-detail/overview/company-overview.component';
import { CompanyImpersonateComponent } from './admin/companies/company-detail/impersonate/company-impersonate.component';
import { CompanyImpersonateAdminComponent } from './admin/companies/company-detail/impersonate/company-impersonate-admin.component';
import { CompanyBulkImportComponent } from './admin/companies/company-detail/bulk-import/company-bulk-import.component';
import { CompanyFeedbackConfigurationComponent } from './admin/companies/company-detail/feedback-configuration/company-feedback-configuration.component';
import { CompanyPlanChangeSettingsComponent } from './admin/companies/company-detail/membership-plan-changes/company-plan-change-settings.component';
import { CompanyCancellationSettingsComponent } from './admin/companies/company-detail/cancellation-refund/company-cancellation-settings.component';
import { CompanyPauseEventsComponent } from './admin/companies/company-detail/membership-pause-events/company-pause-events.component';
import { CompanyFeaturesComponent } from './admin/companies/company-detail/features/company-features.component';
import { AdminFeatureFlagsComponent } from './admin/feature-flags/admin-feature-flags.component';
import { ImpersonationStaffComponent } from './admin/impersonation/pages/staff/impersonation-staff.component';
import { ImpersonationTunnelsComponent } from './admin/impersonation/pages/tunnels/impersonation-tunnels.component';
import { AdminAuditLogComponent } from './admin/audit-log/admin-audit-log.component';
import { AdminTenancyReconcileComponent } from './admin/tenancy-reconcile/admin-tenancy-reconcile.component';
import { GateEventsComponent } from './admin/gate-events/gate-events.component';
import { PlateLookupComponent } from './admin/plate-lookup/plate-lookup.component';
import { appDefaultRedirectGuard } from './guards/app-default-redirect.guard';

const campaignEditorRoute = { data: { hideHeader: true } };

export const routes: Routes = [
  { path: 'docs', component: DocsComponent },
  { path: 'login', component: LoginComponent },
  /** Public SMS entry: /feedback?slug=…&feedbackId=…&token=… (must stay outside AuthGuard). */
  { path: 'feedback', component: FeedbackFormPageComponent },
  /** Public SMS entry: /receipt?token=… (must stay outside AuthGuard). */
  { path: 'receipt', component: ReceiptPageComponent },
  /** Public SMS entry (CWS-1835): /prepaid/check-in?token=… shows the prepaid QR (outside AuthGuard). */
  { path: 'prepaid/check-in', component: PrepaidPassPageComponent },
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: 'resend-email', component: ResendEmailComponent },
  { path: 'reset-password', component: ResetPasswordComponent },
  { path: 'bootstrap', component: AuthBootstrapComponent },

  { path: 'tunnel/login', component: TunnelLoginComponent },
  { path: 'tunnel', component: TunnelOrdersComponent, canActivate: [TunnelGuard] },

  { path: 'admin/login', component: AdminLoginComponent },
  {
    path: 'admin',
    component: SuperAdminShellComponent,
    canActivate: [SuperAdminGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', component: AdminDashboardComponent },
      { path: 'customers', component: AdminCustomersComponent },
      { path: 'campaigns', component: AdminCampaignsComponent },
      // CWS-1921: Location Settings moved into company detail → Location; old links
      // (?company=&location=&tab=) land on the same location and sub-tab there.
      { path: 'settings', pathMatch: 'full', redirectTo: legacyLocationSettingsRedirect },
      { path: 'status', component: AdminStatusComponent },
      { path: 'audit-log', component: AdminAuditLogComponent },
      { path: 'tenancy-reconcile', component: AdminTenancyReconcileComponent },
      { path: 'feature-flags', component: AdminFeatureFlagsComponent },
      { path: 'plate-lookup', component: PlateLookupComponent },
      // Unlisted diagnostic page (CWS-1295): not in super-admin-side-nav on
      // purpose, reachable only by direct URL. Still guarded by SuperAdminGuard
      // above like every other child route here.
      { path: 'gate-events', component: GateEventsComponent },

      { path: 'companies', component: AdminCompaniesComponent },
      { path: 'companies/new', component: AdminCompanyOnboardComponent },
      {
        path: 'companies/:companyId',
        component: CompanyDetailComponent,
        children: [
          { path: '', redirectTo: 'overview', pathMatch: 'full' },
          { path: 'overview', component: CompanyOverviewComponent },
          { path: 'location', component: CompanyLocationComponent },
          { path: 'bulk-import', component: CompanyBulkImportComponent },
          { path: 'feedback-configuration', component: CompanyFeedbackConfigurationComponent },
          { path: 'membership-plan-changes', component: CompanyPlanChangeSettingsComponent },
          { path: 'cancellation-refund', component: CompanyCancellationSettingsComponent },
          { path: 'membership-pause-events', component: CompanyPauseEventsComponent },
          { path: 'features', component: CompanyFeaturesComponent },
          { path: 'audit-log', component: AdminAuditLogComponent },
          {
            path: 'impersonate',
            component: CompanyImpersonateComponent,
            children: [
              { path: '', redirectTo: 'admin', pathMatch: 'full' },
              { path: 'admin', component: CompanyImpersonateAdminComponent },
              { path: 'staff', component: ImpersonationStaffComponent },
              { path: 'tunnel', component: ImpersonationTunnelsComponent },
            ],
          },
        ],
      },

      // Legacy paths kept alive so existing bookmarks resolve. These pages used to
      // open with their own company picker, so the companies list is the honest
      // landing point — the company is now chosen there.
      { path: 'bulk-import', redirectTo: 'companies', pathMatch: 'full' },
      { path: 'onboarding', redirectTo: 'companies', pathMatch: 'full' },
      { path: 'impersonation', redirectTo: 'companies', pathMatch: 'full' },
      { path: 'impersonation/shops', redirectTo: 'companies', pathMatch: 'full' },
      { path: 'impersonation/staff', redirectTo: 'companies', pathMatch: 'full' },
      { path: 'impersonation/tunnels', redirectTo: 'companies', pathMatch: 'full' },
    ],
  },

  {
    path: '',
    component: AppShellComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        children: [],
        canActivate: [appDefaultRedirectGuard],
      },
      { path: 'dashboard', component: DashboardComponent, canActivate: [RoleGuard] },
      { path: 'customer', component: CustomerComponent, canActivate: [RoleGuard] },
      { path: 'package', component: PackageComponent, canActivate: [RoleGuard] },
      { path: 'discount', component: DiscountComponent, canActivate: [RoleGuard] },
      { path: 'employee', component: EmployeeComponent, canActivate: [RoleGuard] },
      { path: 'fleet', component: FleetComponent, canActivate: [RoleGuard] },
      { path: 'campaign', component: CampaignComponent, canActivate: [RoleGuard] },
      { path: 'analytics', component: AnalyticsComponent, canActivate: [RoleGuard] },
      { path: 'reports', component: ReportsComponent, canActivate: [RoleGuard] },
      {
        path: 'payments',
        component: PaymentsComponent,
        canActivate: [RoleGuard],
        children: [
          { path: '', redirectTo: 'payment-dashboard', pathMatch: 'full' },
          { path: 'payment-dashboard', component: PaymentDashboardComponent },
          // Retired: /payments/payouts now falls through to the catch-all route.
          // { path: 'payouts', component: PayoutsComponent },
          { path: 'payout-tracker', component: PayoutTrackerComponent },
        ],
      },
      { path: 'car-wash-website', component: CarWashWebsiteComponent, canActivate: [RoleGuard] },
      {
        path: 'campaign-editor',
        component: CampaignEditorComponent,
        canActivate: [RoleGuard],
        ...campaignEditorRoute,
      },
      {
        path: 'campaign-editor/:tab',
        component: CampaignEditorComponent,
        canActivate: [RoleGuard],
        ...campaignEditorRoute,
      },
      {
        path: 'campaign-editor/:tab/:section',
        component: CampaignEditorComponent,
        canActivate: [RoleGuard],
        ...campaignEditorRoute,
      },
      { path: 'attendance', component: AttendanceComponent, canActivate: [RoleGuard] },
      { path: 'camera', component: CameraComponent, canActivate: [RoleGuard] },
      { path: 'appointments', component: AppointmentsWrapperComponent, canActivate: [RoleGuard] },
      { path: 'orders', component: OrdersWrapperComponent, canActivate: [RoleGuard] },
      { path: 'notifications', component: NotificationsListComponent, canActivate: [RoleGuard] },
      { path: 'rewards', component: RewardsComponent, canActivate: [RoleGuard] },
      { path: 'sales', component: SalesComponent, canActivate: [RoleGuard] },
      {
        path: 'cash-reconciliation',
        component: StaffCashReconciliationComponent,
        canActivate: [RoleGuard],
      },
      { path: 'profile', component: ProfileComponent, canActivate: [RoleGuard] },
      { path: 'settings', component: ProfileComponent, canActivate: [RoleGuard] },
    ],
  },

  {
    path: 'website-builder',
    component: WebsiteBuilderComponent,
    canActivate: [AuthGuard, RoleGuard],
  },
  { path: 'website-preview/:id', component: WebsitePreviewPageComponent, canActivate: [AuthGuard] },
  {
    path: 'website-preview/:id/:pageType',
    component: WebsitePreviewPageComponent,
    canActivate: [AuthGuard],
  },
  { path: 'preview', component: WebsitePreviewPageComponent, canActivate: [AuthGuard] },
  { path: 'email-preview', component: EmailPreviewPageComponent, canActivate: [AuthGuard] },

  { path: 'public-stream/:id', component: PublicStreamComponent },
  { path: 'landing-page', component: SmsCampaignLandingPageComponent },
  { path: 'sms-campaign-success-page', component: SmsCampaignSuccessPageComponent },
  /** Legacy SMS vanity path: /{CarwashName}/feedback?slug=…&feedbackId=…&token=… */
  { path: ':carwashSlug/feedback', component: FeedbackFormPageComponent },

  {
    path: '',
    component: AuthBootstrapComponent,
    canActivate: [initialRedirectGuard],
    pathMatch: 'full',
  },
  { path: '**', component: AuthBootstrapComponent, canActivate: [initialRedirectGuard] },
];
